import { createElement, useCallback, useContext, useState } from 'react';
import { useContextSelector } from 'use-context-selector';

import type { JobFormValues } from '../EditEnrichedVacancyForm/EditEnrichedVacancy.types';

import { reachoutClient } from 'clients/ReachoutClient/ReachoutClient';
import { logger } from 'config/logger';
import { queryClient } from 'config/queryClient';
import { useGTM } from 'hooks/gtm';
import { EventBus } from 'services/event-bus/EventBus';
import { EnrichVacancyEvent } from 'services/event-bus/EventBus.types';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { useCreateSnackbar } from 'shared/legacy/contexts/SnackbarContext/useCreateSnackbar';
import { useFeatureToggles } from 'shared/legacy/hooks';
import { getQuestionnaireConfigurationQueryKey } from 'components/ui/organisms/RJCommunicationHub/useQuestionnaireConfiguration';
import { getJobImprovedCacheKey } from 'shared/legacy/hooks/useJobImprovedQuery';
import { GlobalDialogType } from 'stores/global-dialogs/global-dialogs.store.types';
import { useGlobalDialog } from 'stores/global-dialogs/hooks/useGlobalDialog';
import { QueryKey } from 'types/query';
import { generateUuidv4 } from 'utils/shared/generateUuidv4';
import { syncCampaignConnectedAtsVacancy } from '@/shared/lib/syncCampaignConnectedAtsVacancy';
import { jobImprovedToVacancy } from 'utils/vacancyEnrichedUtils';
import { useEnrichedVacancyModalSettings } from '../../hooks/useEnrichedVacancyModalSettings';
import {
  calculateFormCompletionPercentage,
  mapJobFormValuesToJobImprovedNestedInput,
} from '../../utils/EnrichVacancyModal.mappers';
import { EditEnrichedVacancyContext } from '../EditEnrichedVacancyForm/EditEnrichedVacancyContext/EditEnrichedVacancyContext';
import { useGenerate } from '../EditEnrichedVacancyForm/useGenerate';
import { EnrichVacancyModalContext } from '../EnrichVacancyModalContext/EnrichVacancyModalContext';
import { EnrichVacancyModalType } from '../EnrichVacancyModalContext/EnrichVacancyModalContext.types';

export const useSubmit = () => {
  const enrichVacancyModal = useGlobalDialog(GlobalDialogType.EnrichVacancy);
  const vacancy = useContextSelector(EnrichVacancyModalContext, (s) => s.vacancy);
  const modalType = useContextSelector(EnrichVacancyModalContext, (s) => s.modalType);
  const createSnackbar = useCreateSnackbar();
  const { dictionary } = useContext(LocalizationContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const gtm = useGTM();

  const { isJobPreviewTabVisible } = useEnrichedVacancyModalSettings();
  const jobPreviewRef = useContextSelector(EditEnrichedVacancyContext, (s) => s.jobPreview.ref);
  const enrichedVersionRef = useContextSelector(EditEnrichedVacancyContext, (s) => s.enrichedVersion.ref);

  const { isFeatureToggleEnabled } = useFeatureToggles();

  const isJobPreviewFeatureEnabled = isFeatureToggleEnabled((f) => f.RJ_COMMUNICATION_HUB);

  const {
    handleGenerateJobPreview: requestGenerateJobPreview,
    handleGenerateEnrichedVacancy: requestGenerateEnrichedVacancy,
  } = useGenerate();

  const handleCreate = useCallback(
    async (formData: JobFormValues, jobPreviewValue?: string) => {
      try {
        const createInput = mapJobFormValuesToJobImprovedNestedInput(formData);

        if (vacancy) {
          if (vacancy.type === 'INDEED') {
            createInput.job_indeed_id = vacancy.job_indeed_id;
            createInput.origin_job_title = vacancy.origin_job_title ?? vacancy.job_title ?? undefined;
          } else if (vacancy.type === 'ATS_RAW') {
            createInput.job_ats_id = vacancy.job_ats_id;
            createInput.origin_job_title = vacancy.origin_job_title ?? vacancy.job_title ?? undefined;
          }

          if (vacancy.is_improved) {
            createInput.quality_percent = vacancy.quality_percent ?? undefined;
          }

          if ('summary' in vacancy && typeof vacancy.summary === 'string') {
            createInput.summary = vacancy.summary;
          }

          if (vacancy.job_indeed_id && vacancy.type === 'INDEED' && vacancy.originalIndeedJob?.description) {
            createInput.origin_text = vacancy.originalIndeedJob.description;
          }
          // TODO: Verify ldater
          // else if (creationData?.jobDescription) {
          //   createInput.origin_text = creationData.jobDescription.description;
          // }
        } else if (modalType === EnrichVacancyModalType.Indeed) {
          createInput.job_indeed_id = generateUuidv4();
          createInput.type = 'INDEED';
          // TODO: Verify ldater
          // createInput.origin_job_title = vacancy;
          // createInput.origin_text = creationData?.jobDescription?.description;
        }

        const formCompletionPercentage = calculateFormCompletionPercentage(formData);
        if (formCompletionPercentage > (createInput?.quality_percent ?? 0)) {
          createInput.quality_percent = formCompletionPercentage;
        }

        const response = await reachoutClient.createJobImproved({
          ...createInput,
          ...(jobPreviewValue ? { job_preview: jobPreviewValue } : {}),
        });
        const dbId = response.data.id;

        if (response.status === 200 || response.status === 201) {
          queryClient.removeQueries([QueryKey.useEnrichedVacanciesQuery], { exact: false });
          queryClient.resetQueries([QueryKey.useGetJobImproved, { db_id: dbId }], { exact: false });
          queryClient.removeQueries([QueryKey.useQuestionnairePreviewQuery, { jobId: dbId }], { exact: false });
          queryClient.invalidateQueries({ queryKey: [QueryKey.useJobImprovedListQuery] });

          enrichVacancyModal.close();

          const enrichedVacancy = jobImprovedToVacancy(response.data);

          await syncCampaignConnectedAtsVacancy(enrichedVacancy);

          EventBus.emit(EnrichVacancyEvent.Selected, {
            vacancy: {
              ...enrichedVacancy,
              ...(enrichedVacancy.type === 'INDEED' && vacancy?.type === 'INDEED'
                ? { originalIndeedJob: vacancy.originalIndeedJob }
                : {}),
            },
          });
        }
      } catch (error) {
        console.error('Failed to create job:', error);

        createSnackbar(error instanceof Error ? error.message : '', {
          variant: 'danger',
          title: dictionary.somethingWentWrong,
        });
      }
    },
    [vacancy, modalType],
  );

  const handleUpdate = useCallback(
    async (formData: JobFormValues, jobPreviewValue?: string) => {
      try {
        if (!vacancy) {
          // error D92M1LS - no vacancy selected while trying to update job
          // this scenario should not happen, however, if it does, we should inform the user that something went wrong
          // this is a temporary solution to avoid the user from being stuck in a loop
          logger.error('[D92M1LS] No vacancy selected while trying to update job');
          createSnackbar(dictionary.somethingWentWrong, {
            variant: 'danger',
            title: 'Internal error - D92M1LS',
          });
          return;
        }

        const updateInput = mapJobFormValuesToJobImprovedNestedInput(formData);

        if (vacancy.type === 'ATS_RAW' || vacancy.type === 'ATS') {
          updateInput.job_ats_id = vacancy.job_ats_id;
        } else if (vacancy.type === 'INDEED') {
          updateInput.job_indeed_id = vacancy.job_indeed_id;
        }
        const formCompletionPercentage = calculateFormCompletionPercentage(formData);

        if (formCompletionPercentage > (updateInput?.quality_percent ?? 0)) {
          updateInput.quality_percent = formCompletionPercentage;
        }

        if (updateInput && vacancy.db_id) {
          const response = await reachoutClient.patchJobImproved(vacancy.db_id, {
            ...updateInput,
            ...(jobPreviewValue ? { job_preview: jobPreviewValue } : {}),
          });

          if (response.status === 200 || response.status === 201) {
            const dbId = Number(vacancy.db_id);

            queryClient.removeQueries([QueryKey.useEnrichedVacanciesQuery], { exact: false });
            queryClient.resetQueries([QueryKey.useGetJobImproved, { db_id: dbId }], { exact: false });
            queryClient.removeQueries([QueryKey.useQuestionnairePreviewQuery, { jobId: dbId }], { exact: false });
            queryClient.invalidateQueries(getJobImprovedCacheKey(dbId));
            queryClient.invalidateQueries({ queryKey: getQuestionnaireConfigurationQueryKey(dbId) });
            queryClient.invalidateQueries({ queryKey: [QueryKey.useJobImprovedListQuery] });

            const enrichedVacancy = jobImprovedToVacancy(response.data);

            await syncCampaignConnectedAtsVacancy(enrichedVacancy);

            EventBus.emit(EnrichVacancyEvent.Selected, {
              vacancy: {
                ...enrichedVacancy,
                ...(enrichedVacancy.type === 'INDEED' && vacancy.type === 'INDEED'
                  ? { originalIndeedJob: vacancy.originalIndeedJob }
                  : {}),
              },
            });

            enrichVacancyModal.close();
          }
        }
      } catch (error) {
        console.error('Failed to update job:', error);

        createSnackbar(error instanceof Error ? error.message : '', {
          variant: 'danger',
          title: dictionary.somethingWentWrong,
        });
      }
    },
    [vacancy],
  );

  const handleSaveWithJobPreview = useCallback(async () => {
    if (!enrichedVersionRef.current) return;

    const isValid = await enrichedVersionRef.current.trigger();

    if (!isValid) {
      enrichedVersionRef.current.focusFormTab();
      return;
    }

    const formValues = await getOrGenerateEnrichedVacancy();
    const jobPreview = await getOrGenerateJobPreview();

    if (!formValues || (!jobPreview && isJobPreviewTabVisible)) {
      let errorMessage = '';

      if (!formValues) errorMessage += dictionary.couldNotGenerateEnrichedVacancy;
      if (!jobPreview) errorMessage += `${errorMessage ? '<br />' : ''} ${dictionary.couldNotGenerateJobPreview}`;

      createSnackbar(createElement('span', { dangerouslySetInnerHTML: { __html: errorMessage } }), {
        variant: 'danger',
        title: dictionary.somethingWentWrong,
      });

      return;
    }

    if (vacancy?.is_improved) {
      await handleUpdate(formValues, jobPreview);
    } else {
      await handleCreate(formValues, jobPreview);
    }

    return;
  }, [vacancy, isJobPreviewTabVisible, dictionary]);

  const handleSaveWithoutJobPreview = useCallback(async () => {
    if (!enrichedVersionRef.current) return;

    const isValid = await enrichedVersionRef.current.trigger();

    if (!isValid) {
      enrichedVersionRef.current.focusFormTab();
      return;
    }

    const formValues = await enrichedVersionRef.current.getValues();

    if (vacancy?.is_improved) {
      await handleUpdate(formValues);
    } else {
      await handleCreate(formValues);
    }

    return;
  }, [vacancy, isJobPreviewTabVisible, dictionary]);

  const handleSaveAndSubmit = useCallback(async () => {
    setIsSubmitting(true);

    try {
      if (isJobPreviewFeatureEnabled) {
        await handleSaveWithJobPreview();
      } else {
        await handleSaveWithoutJobPreview();
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [handleSaveWithoutJobPreview, handleSaveWithJobPreview]);

  const getOrGenerateJobPreview = useCallback(async () => {
    if (!isJobPreviewTabVisible || !jobPreviewRef.current || !enrichedVersionRef.current) return '';

    const shouldGenerateJobPreview =
      (enrichedVersionRef.current.getIsDirty() && !jobPreviewRef.current.getIsDirty()) ||
      jobPreviewRef.current.getValue() === '';

    if (jobPreviewRef.current?.getHasUnsavedChanges() || shouldGenerateJobPreview) {
      gtm.jobPreviewEdited({ editedBy: shouldGenerateJobPreview ? 'auto-generated' : 'user' });
    }

    if (shouldGenerateJobPreview) {
      return requestGenerateJobPreview();
    }

    return jobPreviewRef.current.getHTML();
  }, [isJobPreviewTabVisible]);

  const getOrGenerateEnrichedVacancy = useCallback(async () => {
    if (!enrichedVersionRef.current) return null;

    const shouldGenerateEnrichedVacancy = isJobPreviewTabVisible
      ? !enrichedVersionRef.current.getIsDirty() && jobPreviewRef.current?.getIsDirty()
      : false;

    if (shouldGenerateEnrichedVacancy) {
      const generatedEnrichedVacancy = await requestGenerateEnrichedVacancy();

      enrichedVersionRef.current.reset(generatedEnrichedVacancy);

      const isValid = await new Promise((res) => {
        setTimeout(async () => {
          if (!enrichedVersionRef.current) return;

          const isValid = await enrichedVersionRef.current.trigger();

          res(isValid);
        }, 100);
      });

      if (!isValid) {
        enrichedVersionRef.current.focusFormTab();
        return null;
      }
    }

    return enrichedVersionRef.current.getValues();
  }, [isJobPreviewTabVisible]);

  return { handleSaveAndSubmit, isSubmitting };
};
